import { create } from "zustand";
import { notificationsConfig } from "../config/notifications.config.js";
import { findNotificationInList } from "../lib/apply-notification-read-state.js";
import { countUnreadNotifications, isUnreadNotification } from "../lib/count-unread-notifications.js";
import { isEssentialReadNotification } from "../lib/filter-notifications-by-inbox-tab.js";
import { extractNextCursor } from "../lib/normalize-notifications-list.js";
import { toNotificationVmList } from "../lib/to-notification-vm.js";
import notificationService from "../services/notification-service.js";

/**
 * Store do inbox de notificações — dois slices independentes: pendentes e lidas.
 * Única fonte de verdade; sem persist.
 *
 * @typedef {Object} NotificationsStoreState
 * @property {Object[]} pendingItems   — VMs não lidas (virtual + persistidas)
 * @property {string|null} pendingNextCursor
 * @property {boolean} pendingIsLoading
 * @property {string|null} pendingError
 * @property {Object[]} readItems      — VMs essenciais lidas
 * @property {string|null} readNextCursor
 * @property {boolean} readIsLoading
 * @property {string|null} readError
 * @property {number} unreadCount
 * @property {boolean} isMarkingRead
 */

export const useNotificationsStore = create((set, get) => ({
  // ── Pendentes ──────────────────────────────────────────────────────────────
  pendingItems: [],
  pendingNextCursor: null,
  pendingIsLoading: false,
  pendingError: null,

  // ── Lidas ──────────────────────────────────────────────────────────────────
  readItems: [],
  readNextCursor: null,
  readIsLoading: false,
  readError: null,

  // ── Compartilhado ──────────────────────────────────────────────────────────
  unreadCount: 0,
  isMarkingRead: false,

  /**
   * Busca (ou recarrega) a página inicial de não lidas.
   * Substitui `pendingItems` do zero — usado pelo polling e reload.
   * @param {{ limit?: number|string }} [params]
   * @param {RequestInit} [requestOptions]
   */
  fetchPending: async (params = {}, requestOptions) => {
    set({ pendingIsLoading: true, pendingError: null });
    try {
      const response = await notificationService.list(
        { limit: params?.limit ?? notificationsConfig.defaultLimit },
        requestOptions,
      );

      if (!response?.success) {
        set({
          pendingIsLoading: false,
          pendingError: response?.message || "Erro ao carregar notificações",
        });
        return response;
      }

      const allVms = toNotificationVmList(response.data?.notifications);
      const pending = allVms.filter(isUnreadNotification);
      const unreadCount =
        typeof response.data?.unreadCount === "number"
          ? response.data.unreadCount
          : countUnreadNotifications(pending);

      set({
        pendingItems: pending,
        pendingNextCursor: extractNextCursor(response),
        unreadCount,
        pendingIsLoading: false,
        pendingError: null,
      });

      return response;
    } catch (err) {
      const message = err?.message || "Erro ao carregar notificações";
      set({ pendingIsLoading: false, pendingError: message });
      return { success: false, message, status: 500, code: "UNKNOWN_ERROR", error: err };
    }
  },

  /**
   * Anexa a próxima página de não lidas usando o cursor de pendentes.
   * Silencioso — sem alteração de `pendingIsLoading`.
   */
  fetchMorePending: async () => {
    const { pendingNextCursor, pendingItems } = get();
    if (!pendingNextCursor) return null;

    const response = await notificationService.list({ cursor: pendingNextCursor });
    if (!response?.success) return response;

    const moreVms = toNotificationVmList(response.data?.notifications ?? []);
    const morePending = moreVms.filter(isUnreadNotification);
    const existingIds = new Set(pendingItems.map((n) => n.id).filter(Boolean));
    const unique = morePending.filter((n) => n.id && !existingIds.has(n.id));

    set({
      pendingItems: [...pendingItems, ...unique],
      pendingNextCursor: extractNextCursor(response),
    });

    return response;
  },

  /**
   * Busca (ou recarrega) a página inicial de essenciais lidas — lazy, sob demanda.
   * @param {{ limit?: number|string }} [params]
   */
  fetchRead: async (params = {}) => {
    set({ readIsLoading: true, readError: null });
    try {
      const response = await notificationService.list({
        limit: params?.limit ?? notificationsConfig.readInboxLimit,
        hideNonEssentialRead: true,
      });

      if (!response?.success) {
        set({
          readIsLoading: false,
          readError: response?.message || "Erro ao carregar notificações lidas",
        });
        return response;
      }

      const allVms = toNotificationVmList(response.data?.notifications ?? []);
      const read = allVms.filter(isEssentialReadNotification);

      set({
        readItems: read,
        readNextCursor: extractNextCursor(response),
        readIsLoading: false,
        readError: null,
      });

      return response;
    } catch (err) {
      const message = err?.message || "Erro ao carregar notificações lidas";
      set({ readIsLoading: false, readError: message });
      return { success: false, message, status: 500, code: "UNKNOWN_ERROR", error: err };
    }
  },

  /**
   * Anexa a próxima página de essenciais lidas usando o cursor de lidas.
   * Silencioso — sem alteração de `readIsLoading`.
   */
  fetchMoreRead: async () => {
    const { readNextCursor, readItems } = get();
    if (!readNextCursor) return null;

    const response = await notificationService.list({ cursor: readNextCursor });
    if (!response?.success) return response;

    const moreVms = toNotificationVmList(response.data?.notifications ?? []);
    const moreRead = moreVms.filter(isEssentialReadNotification);
    const existingIds = new Set(readItems.map((n) => n.id).filter(Boolean));
    const unique = moreRead.filter((n) => n.id && !existingIds.has(n.id));

    set({
      readItems: [...readItems, ...unique],
      readNextCursor: extractNextCursor(response),
    });

    return response;
  },

  /**
   * Remove o item de `pendingItems` otimisticamente e devolve snapshot para rollback.
   * @param {string} notificationId
   * @returns {Object|null} snapshot pré-otimista
   */
  applyMarkAsReadOptimistic: (notificationId) => {
    const id = typeof notificationId === "string" ? notificationId.trim() : "";
    if (!id) return null;

    const { pendingItems, unreadCount } = get();
    const snapshot = findNotificationInList(pendingItems, id);
    if (!snapshot) return null;

    const nextUnreadCount = isUnreadNotification(snapshot)
      ? Math.max(0, unreadCount - 1)
      : unreadCount;

    set({
      pendingItems: pendingItems.filter((n) => n.id !== id),
      unreadCount: nextUnreadCount,
      isMarkingRead: true,
    });

    return snapshot;
  },

  /**
   * Restaura o item em `pendingItems` (rollback pós-falha do POST).
   * @param {string} notificationId
   * @param {Object|null} snapshot
   */
  revertNotification: (notificationId, snapshot) => {
    const id = typeof notificationId === "string" ? notificationId.trim() : "";
    if (!id || !snapshot) {
      set({ isMarkingRead: false });
      return;
    }

    const { pendingItems, unreadCount } = get();
    const nextUnreadCount = isUnreadNotification(snapshot)
      ? unreadCount + 1
      : unreadCount;

    set({
      pendingItems: [snapshot, ...pendingItems.filter((n) => n.id !== id)],
      unreadCount: nextUnreadCount,
      isMarkingRead: false,
    });
  },

  /** @param {string|null} [message] */
  clearError: (message = null) => {
    set({ pendingError: message });
  },

  finishMarkAsRead: () => {
    set({ isMarkingRead: false });
  },

  // ── Aliases de retrocompatibilidade ────────────────────────────────────────

  /** @deprecated use `fetchPending` */
  fetchNotifications: async (params, requestOptions) =>
    get().fetchPending(params, requestOptions),
}));
